import { fetchEventSource } from '@microsoft/fetch-event-source'
import { isUnauthorizedError, normalizeError } from '../utils/errors.js'

// Metrics stream endpoint - SSE updates every 5 seconds
// Use CEP API base URL, similar to how statements and monitor endpoints work
const DEFAULT_CEP_BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_CEP_API_BASE_URL) || '/api'
const DEFAULT_STREAM_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_METRICS_STREAM_URL) ||
  `${DEFAULT_CEP_BASE_URL}/metrics/load`
const DEFAULT_RETRY = {
  initialDelayMs: 500,    // Start reconnecting after 500ms
  maxDelayMs: 5_000,      // Cap at 5 seconds instead of 15
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const shouldRetry = (error) => {
  if (isUnauthorizedError(error)) {
    return false
  }
  const normalized = normalizeError(error)
  if (normalized.code === 'STREAM_CLOSED') {
    return true
  }
  if (normalized.status && normalized.status >= 400 && normalized.status < 500) {
    return false
  }
  return true
}

const buildStreamUrl = ({ streamUrl, instanceId }) => {
  // API endpoint: GET /api/metrics/load/{instanceId}
  console.log('[MetricsStreamService] buildStreamUrl called with:', { streamUrl, instanceId })
  if (!instanceId) {
    throw new Error('Instance ID is required for metrics stream')
  }
  const url = `${streamUrl.replace(/\/$/, '')}/${encodeURIComponent(instanceId)}`
  console.log('[MetricsStreamService] Built URL:', url)
  return url
}

export class MetricsStreamService {
  constructor({
    streamUrl = DEFAULT_STREAM_URL,
    getAuthHeaders = () => ({}),
    retry = {},
  } = {}) {
    this.streamUrl = streamUrl
    this.getAuthHeaders = getAuthHeaders
    this.retryConfig = {
      initialDelayMs: retry.initialDelayMs ?? DEFAULT_RETRY.initialDelayMs,
      maxDelayMs: retry.maxDelayMs ?? DEFAULT_RETRY.maxDelayMs,
    }
    this.abortController = null
    this.lastEventId = null
    this.isActive = false
    this.reconnectAttempt = 0
    this.handlers = {}
    this.instanceId = null
    this.pendingRun = null
  }

  start(options = {}) {
    console.log('[MetricsStreamService] start() called with options:', options)
    if (!options.instanceId) {
      throw new Error('instanceId is required to start metrics stream')
    }
    this.handlers = {
      onMetrics: options.onMetrics,
      onError: options.onError,
      onStatusChange: options.onStatusChange,
      onReconnectScheduled: options.onReconnectScheduled,
    }
    this.instanceId = options.instanceId
    console.log('[MetricsStreamService] Set instanceId to:', this.instanceId)
    this.isActive = true
    this.reconnectAttempt = 0
    this.pendingRun = this.runLoop()
    return () => this.stop()
  }

  async runLoop() {
    while (this.isActive) {
      try {
        await this.connectOnce()
        if (!this.isActive) {
          break
        }
        // If connectOnce resolves naturally, schedule immediate retry.
        this.reconnectAttempt += 1
        const delay = Math.min(
          this.retryConfig.initialDelayMs * 2 ** Math.max(0, this.reconnectAttempt - 1),
          this.retryConfig.maxDelayMs,
        )
        this.handlers.onStatusChange?.('reconnecting', {
          attempt: this.reconnectAttempt,
          delay,
        })
        this.handlers.onReconnectScheduled?.({
          delay,
          attempt: this.reconnectAttempt,
        })
        await wait(delay)
      } catch (error) {
        if (!this.isActive) {
          break
        }

        if (error?.name === 'AbortError') {
          if (this.isActive) {
            this.reconnectAttempt = 0
            continue
          }
          break
        }

        const normalized = normalizeError(error)
        this.handlers.onError?.(normalized)

        if (!shouldRetry(normalized)) {
          this.handlers.onStatusChange?.('error', normalized)
          this.isActive = false
          break
        }

        this.reconnectAttempt += 1
        const delay = Math.min(
          this.retryConfig.initialDelayMs * 2 ** Math.max(0, this.reconnectAttempt - 1),
          this.retryConfig.maxDelayMs,
        )
        this.handlers.onStatusChange?.('reconnecting', {
          attempt: this.reconnectAttempt,
          delay,
        })
        this.handlers.onReconnectScheduled?.({
          delay,
          attempt: this.reconnectAttempt,
        })
        await wait(delay)
      }
    }

    this.handlers.onStatusChange?.('stopped')
  }

  async connectOnce() {
    const url = buildStreamUrl({
      streamUrl: this.streamUrl,
      instanceId: this.instanceId,
    })

    const headers = {
      ...this.getAuthHeaders(),
    }

    console.log('[MetricsStreamService] Auth headers:', {
      hasApiKey: Boolean(headers['x-api-key']),
      hasWriteKey: Boolean(headers['writekey']),
    })

    if (this.lastEventId) {
      headers['Last-Event-ID'] = this.lastEventId
    }

    // Note: Do NOT set Accept header here - fetchEventSource library sets it automatically
    // Setting it manually causes duplicate "text/event-stream, text/event-stream" which causes 406
    headers['Cache-Control'] = 'no-cache'

    console.log('[MetricsStreamService] Connecting to URL with headers:', { url, headers: Object.keys(headers) })

    this.abortController = new AbortController()

    this.handlers.onStatusChange?.(
      this.reconnectAttempt > 0 ? 'reconnecting' : 'connecting',
      { attempt: this.reconnectAttempt },
    )

    const self = this

    await fetchEventSource(url, {
      method: 'GET',
      headers,
      signal: this.abortController.signal,
      async onopen(response) {
        if (response.status === 401 || response.status === 403) {
          const error = new Error('Unauthorized')
          error.status = response.status
          throw error
        }

        if (!response.ok) {
          const error = new Error(`Stream responded with ${response.status}`)
          error.status = response.status
          throw error
        }

        // Connection is established - mark as connected immediately
        self.reconnectAttempt = 0
        self.handlers.onStatusChange?.('connected')
      },
      onmessage: (event) => {
        if (!event.data) {
          return
        }

        this.lastEventId = event.id ?? this.lastEventId

        let parsedData = null

        try {
          parsedData = JSON.parse(event.data)
        } catch (_error) {
          parsedData = event.data
        }

        // SSE sends 'metrics' event type
        if (event.event === 'metrics' || !event.event) {
          this.handlers.onMetrics?.({
            data: parsedData,
            id: event.id ?? null,
            receivedAt: Date.now(),
            raw: event,
          })
        }
      },
      onclose: () => {
        const error = new Error('Stream closed unexpectedly')
        error.code = 'STREAM_CLOSED'
        throw error
      },
      onerror: (error) => {
        throw error ?? new Error('Stream encountered an error')
      },
      openWhenHidden: true,
    })
  }

  restart() {
    if (!this.isActive) {
      return
    }
    this.reconnectAttempt = 0
    this.abortController?.abort()
  }

  manualReconnect() {
    if (!this.isActive) {
      return
    }
    this.reconnectAttempt = 0
    this.lastEventId = null
    this.abortController?.abort()
  }

  stop() {
    this.isActive = false
    this.abortController?.abort()
    this.abortController = null
    this.lastEventId = null
  }

  getLastEventId() {
    return this.lastEventId
  }
}

export const createMetricsStreamService = (options) =>
  new MetricsStreamService(options)
